import React, { useCallback } from 'react'
import { pathOr, isEmpty } from 'ramda'
import { Segment, FormField, Select, CommissionSplit, CurrencyField, PercentageField } from 'components'
import { portfolioApiUtils } from 'modules/api/portfolio'
import { formUtils } from 'utils'
import { commissionUndertakings, leaseTypes } from 'modules/api/portfolio/constants'
import { SelectOption, PaymentRule, PortfolioRentInvoice, ManagementFeeValues } from '../types'
import { ErrorPosition } from 'views/components/atoms/TextField/text-field.types'

interface ManagementFeeFormProps {
  /** Formik `setFieldTouched` */
  setFieldTouched: (field: string, touched?: boolean) => void
  /** Formik `setFieldValue` */
  setFieldValue: (field: string, value: any) => void
  /** Formik `handleChange` */
  handleChange: (e: React.ChangeEvent<any>) => void
  /** Formik `handleBlur` */
  handleBlur: (e: React.FocusEvent<any>) => void
  defaultResultSets?: any[][]
  /** Formik values */
  values: ManagementFeeValues
  rentAmount: number | string
  errors: Record<string, any>
  touched?: Record<string, any>
  submitCount?: number
  portfolioRentInvoice?: PortfolioRentInvoice
}

const options: SelectOption[] = [
  {
    value: commissionUndertakings.variable,
    label: 'Percentage',
  },
  {
    value: commissionUndertakings.fixed,
    label: 'Fixed amount',
  },
]

const ManagementFeeForm = ({
  setFieldTouched,
  setFieldValue,
  handleChange,
  handleBlur,
  values,
  rentAmount,
  errors,
  defaultResultSets,
  touched = {},
  submitCount = 0,
  portfolioRentInvoice,
}: ManagementFeeFormProps) => {
  const getFieldError = formUtils.getFieldError(submitCount, touched, errors)

  const getRentInvoicePaymentRulesTotal = (commissionAmount: number | string): void => {
    const paymentRules = pathOr<PaymentRule[]>([], ['paymentRules'], portfolioRentInvoice)
    const paymentRulesTotal = portfolioApiUtils.getPaymentRulesAmountExRent(paymentRules, commissionAmount)
    const paymentRulesTotalExCommission = portfolioApiUtils.getPaymentRulesAmountExRent(paymentRules, 0)

    setFieldValue('managementFee.paymentRulesTotal', paymentRulesTotal)
    setFieldValue('managementFee.paymentRulesTotalExCommission', paymentRulesTotalExCommission)
    setFieldValue('procurementFee.paymentRulesTotal', paymentRulesTotal)
    setFieldValue('procurementFee.paymentRulesTotalExCommission', paymentRulesTotalExCommission)
    setFieldValue('procurementFee.monthlyRentAmount', rentAmount)
  }

  const handleTypeChange = (data: SelectOption | null) => {
    if (!data) return
    setFieldValue('managementFee.type', data.value)
    if (isEmpty(rentAmount)) return

    if (data.value === commissionUndertakings.fixed) {
      const netAmount = Number(rentAmount) * (Number(values.percentage.value) / 100)
      setFieldValue('managementFee.netAmount.value', netAmount)
    } else if (data.value === commissionUndertakings.variable) {
      const numericRent = Number(rentAmount)
      const percentage = numericRent !== 0 ? (Number(values.netAmount.value) / numericRent) * 100 : 0
      setFieldValue('managementFee.percentage.value', percentage)
    }
  }

  return (
    <Segment>
      <Segment.Group>
        <Segment.Group horizontal>
          {/* @ts-expect-error - style prop not in Segment type definition */}
          <Segment style={{ width: '40%' } as React.CSSProperties}>
            <h3 style={{ fontSize: '20px' }}>Management Fee</h3>
          </Segment>
          <Segment>
            <Select
              noOutline
              style={{ marginTop: '10px' }}
              name="managementFee.type"
              error={getFieldError('managementFee.type')}
              value={options.find(option => option.value === values.type)}
              onChange={handleTypeChange}
              onBlur={() => setFieldTouched('managementFee.type')}
              options={options}
            />
          </Segment>
        </Segment.Group>
        <Segment.Group horizontal>
          <Segment>
            <div className="rbn--row">
              <div className="rbn--col-md">
                <FormField>
                  <CurrencyField
                    disabled
                    name="managementFee.rentAmount"
                    label="Rental amount"
                    error={getFieldError('managementFee.rentAmount')}
                    value={rentAmount}
                  />
                </FormField>
              </div>
              <div className="rbn--col-md">
                <FormField>
                  {/** @todo convert to VAT Input Control */}
                  {values.type === commissionUndertakings.fixed ? (
                    <CurrencyField
                      includeVatControl
                      vatApplied={values.netAmount.vatable}
                      name="managementFee.netAmount"
                      label="Commission amount"
                      error={getFieldError('managementFee.netAmount')}
                      value={values.netAmount.value}
                      onToggleChange={(vatApplied: boolean) => {
                        setFieldValue('managementFee.netAmount.vatable', vatApplied)
                      }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue('managementFee.netAmount.value', e.target.value)
                        getRentInvoicePaymentRulesTotal(e.target.value)
                      }}
                    />
                  ) : (
                    <PercentageField
                      includeVatControl
                      vatApplied={values.percentage.vatable}
                      name="managementFee.percentage"
                      label={`Percentage ${values.percentage.vatable ? '(incl. VAT)' : ''}`}
                      amount={rentAmount}
                      error={getFieldError('managementFee.percentage')}
                      onFocus={() => setFieldTouched('managementFee.percentage')}
                      value={values.percentage.value}
                      onChange={(e: { floatValue: string }) => {
                        const parsed = parseFloat(e.floatValue)
                        const percentageValue = Number.isNaN(parsed) ? 0 : parsed
                        setFieldValue('managementFee.percentage.value', percentageValue)
                        const commissionAmount = (Number(values.monthlyRentAmount) / 100) * percentageValue
                        getRentInvoicePaymentRulesTotal(commissionAmount)
                      }}
                      onToggleChange={(vatApplied: boolean) =>
                        setFieldValue('managementFee.percentage.vatable', vatApplied)
                      }
                      onBlur={handleBlur}
                      errorPosition={ErrorPosition.bottom}
                    />
                  )}
                </FormField>
              </div>
            </div>
          </Segment>
        </Segment.Group>
        <Segment.Group>
          <Segment>
            <CommissionSplit
              feeName="managementFee"
              values={values}
              leaseType={leaseTypes.managed}
              handleChange={handleChange}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              errors={pathOr({}, ['managementFee', 'splits'], errors)}
              defaultResultSets={defaultResultSets}
            />
          </Segment>
        </Segment.Group>
      </Segment.Group>
    </Segment>
  )
}

export default ManagementFeeForm
